import { ProfileSectionTypes } from 'app/my-account/pages/my-profile/enums';
import ButtonComponent from 'components/button/buttonComponent';
import { FullPageLoader } from 'components/loaderComponent';
import { ModalButtonProps } from 'components/modal/modal.interfaces';
import Modal from 'components/modal/modalComponent';
import { useGetComponent } from 'hooks/useGetComponent';
import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { componentIds } from 'utilities/constants';
import {
  DASHBOARD,
  LEGAL_DOCUMENTS_ROUTE,
  MY_ACCOUNT_HEALTH_QUESTIONNAIRE,
  MY_ACCOUNT_PROFILE,
  MY_PROFILE_VALIDATE_PERSONA,
} from 'utilities/routes';
import { useAuth } from 'auth/context/AuthContext';
import { usePatientProfileContext } from '../../app/my-account/pages/patient/PatientProfileContext';
import { useProtectedModal } from '../../router/patient/ProtectedModalContext';

export interface ProtectedRoutesModalProps {
  children: React.ReactNode;
  manualOpenOnly?: boolean;
}

interface MissingType {
  title: string;
  description: string;
  buttonText: string;
  buttonAction: () => void;
}

const ProtectedRoutesModal: React.FC<ProtectedRoutesModalProps> = ({
  children,
  manualOpenOnly = false,
}) => {
  const { selectedAccount } = useAuth();
  const { open: isOpen, setOpen: setIsOpen } = useProtectedModal();
  const {
    userValidation: validations,
    validationLoading,
    updatePatientValidation,
  } = usePatientProfileContext();
  const { data: locale } = useGetComponent({
    locale: 'en',
    componentId: componentIds.PROTECTED_ROUTE_MODAL,
  });

  const navigate = useNavigate();
  const { pathname, search, hash } = useLocation();

  const missing = useMemo(() => {
    if (!locale) return [];
    const missing: MissingType[] = [];
    if (!validations?.isPersonalInfoValid) {
      missing.push({
        title: locale.noPersonalInfoTitle,
        description: locale.noPersonalInfoDescription,
        buttonText: locale.noPersonalInfoButton,
        buttonAction: () =>
          navigate(
            selectedAccount
              ? `${MY_ACCOUNT_PROFILE}/profile`
              : MY_ACCOUNT_PROFILE,
            {
              state: {
                section: ProfileSectionTypes.PROFILE,
              },
            },
          ),
      });
    }

    if (!validations?.isStateDobValid) {
      missing.push({
        title: locale.noMinorConsentTitle,
        description: locale.noMinorConsentDescription,
        buttonText: locale.noMinorConsentButton,
        buttonAction: () =>
          navigate(MY_ACCOUNT_PROFILE, {
            state: {
              section: ProfileSectionTypes.PROFILE,
            },
          }),
      });
    }

    if (!validations?.isSudReport) {
      missing.push({
        title: 'Notice of Privacy Practices for SUD Records',
        description: 'Please confirm to continue',
        buttonText: 'Accept Notice',
        buttonAction: () => navigate(`${LEGAL_DOCUMENTS_ROUTE}/sud-patient`),
      });
    }

    if (!validations?.isSubstance) {
      missing.push({
        title: 'Consent to use and disclose substance use disorder records',
        description: 'Please confirm to continue',
        buttonText: 'Accept Notice',
        buttonAction: () => navigate(`${LEGAL_DOCUMENTS_ROUTE}/substance`),
      });
    }

    if (!validations?.isContactInfoValid) {
      missing.push({
        title: locale.noContactInfoTitle,
        description: locale.noContactInfoDescription,
        buttonText: locale.noContactInfoButton,
        buttonAction: () =>
          navigate(MY_ACCOUNT_PROFILE, {
            state: {
              section: ProfileSectionTypes.CONTACT,
            },
          }),
      });
    }

    if (!validations?.isQuestionnaireValid) {
      missing.push({
        title: locale.noQuestionnaireTitle,
        description: locale.noQuestionnaireDescription,
        buttonText: locale.noQuestionnaireButton,
        buttonAction: () => navigate(MY_ACCOUNT_HEALTH_QUESTIONNAIRE),
      });
    }

    if (!validations?.isPersonaVerified) {
      missing.push({
        title: locale.noPersonaTitle,
        description: locale.noPersonaDescription,
        buttonText: locale.noPersonaButton,
        buttonAction: () => navigate(MY_PROFILE_VALIDATE_PERSONA),
      });
    }

    return missing;
  }, [locale, validations, navigate, selectedAccount]);

  const handleClose = () => {
    setIsOpen(false);
    if (pathname === DASHBOARD) {
      return;
    }
    navigate(-1);
  };

  useEffect(() => {
    if (!manualOpenOnly && missing.length && !validations?.generalValidation) {
      setIsOpen(true);
      navigate({ pathname, search, hash }, { replace: true });
    }
    if (validations?.generalValidation) {
      setIsOpen(false);
    }
  }, [
    validations,
    navigate,
    pathname,
    missing,
    search,
    hash,
    setIsOpen,
    manualOpenOnly,
  ]);

  useEffect(() => {
    if (!validations?.generalValidation) {
      updatePatientValidation();
    }
  }, [updatePatientValidation, validations?.generalValidation]);

  if (!validations || !locale) return <FullPageLoader />;
  if (validationLoading) return <>{children}</>;

  const Buttons: ModalButtonProps[] = [
    {
      label: locale?.goBack,
      disabled: false,
      onClick: handleClose,
      testID: 'go-back',
    },
  ];

  return (
    <>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          disableBackdropClick
          onClose={handleClose}
          title={locale?.title}
          buttons={Buttons}
        >
          <div className="flex flex-col gap-5 px-4">
            {missing.map(({ buttonAction, buttonText, description, title }) => {
              return (
                <div className="flex flex-row justify-between items-center">
                  <div className="flex flex-col justify-start items-start">
                    <p className="text-lg font-bold">{title}</p>
                    <p className="text-base text-med-gray font-medium">
                      {description}
                    </p>
                  </div>
                  <ButtonComponent onClick={buttonAction} testID={''}>
                    {buttonText}
                  </ButtonComponent>
                </div>
              );
            })}
          </div>
        </Modal>
      )}
      {children}
    </>
  );
};

export default ProtectedRoutesModal;
